import axios from 'axios';
import router from '@/router';

const apiClient = axios.create({
  baseURL: `${process.env.VUE_APP_STRAPI_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  }
});

apiClient.interceptors.request.use(config => {
  const token = localStorage.getItem('jwt');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, error => {
  return Promise.reject(error);
});

apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('jwt');
      localStorage.removeItem('user');
      if (router.currentRoute.value.name !== 'Login') {
        router.push({ name: 'Login' });
      }
    }
    return Promise.reject(error);
  }
);

export default {
  getCampaigns(budgetId = null) {
    const params = {};
    if (budgetId) {
      params['filters[budget][id]'] = budgetId;
    }
    return apiClient.get('/campaigns', { params });
  },
  getCatalogs(campaignId = null, sortBy = null, status = null, budgetId = null) {
    const params = {
      populate: {
        campaign: true,
        pages: {
          populate: {
            active_image_version: {
              populate: 'fichier_jpeg'
            }
          }
        }
      }
    };

    if (campaignId) {
      params['filters[campaign][id]'] = campaignId;
    }

    if (budgetId) {
      params['filters[campaign][budget][id]'] = budgetId;
    }

    if (status) {
      const now = new Date();
      const today = now.toISOString().split('T')[0]; // YYYY-MM-DD

      if (status === 'published') {
        params['filters[publication_date][$lte]'] = today;
      } else if (status === 'pending') {
        params['filters[publication_date][$gt]'] = today;
      }
    }

    if (sortBy) {
      switch (sortBy) {
        case 'name-asc':
          params['sort'] = 'title:asc';
          break;
        case 'name-desc':
          params['sort'] = 'title:desc';
          break;
        case 'publication-date-desc':
          params['sort'] = 'publication_date:desc';
          break;
        case 'publication-date-asc':
          params['sort'] = 'publication_date:asc';
          break;
        case 'creation-date-desc':
          params['sort'] = 'createdAt:desc';
          break;
        case 'creation-date-asc':
          params['sort'] = 'createdAt:asc';
          break;
      }
    }

    return apiClient.get('/supports', { params });
  },
  getCatalog(id) {    
    return apiClient.get(`/supports/${id}`, {
      params: {
        populate: {
          pages: {
            populate: {
              products: {
                populate: 'image'
              },
              active_image_version: {
                populate: 'fichier_jpeg'
              }
            }
          }
        }
      }
    });
  },
  getCatalogWithNotes(id) {
    return apiClient.get(`/supports/${id}`, {
      params: {
        populate: {
          pages: {
            populate: {
              notes: {
                populate: ['attachment', 'user', 'version_image_snapshot', 'version_image_snapshot.fichier_jpeg', 'comments', 'comments.user', 'comments.parent']
              },
              active_image_version: {
                populate: 'fichier_jpeg'
              }
            }
          },
          campaign: {
            populate: 'budget.logo'
          },
          charte: true,
        }
      }
    });
  },
  createNote(data) {
    return apiClient.post('/notes', { data });
  },
  updateNote(id, data) {
    return apiClient.put(`/notes/${id}`, { data });
  },
  deleteNote(id) {
    return apiClient.delete(`/notes/${id}`);
  },
  getComments(noteId) {
    const params = {
      filters: {
        note: {
          id: {
            $eq: noteId,
          },
        },
      },
      populate: ['user', 'parent', 'attachment'],
    };
    return apiClient.get('/comments', { params });
  },
  createComment(data) {
    return apiClient.post('/comments', { data });
  },
  upload(file) {
    const formData = new FormData();
    formData.append('files', file);
    return apiClient.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  login(identifier, password) {
    return apiClient.post('/auth/local', {
      identifier,
      password,
    });
  },

  getProducts() {
    return apiClient.get('/products', { params: { populate: '*' } });
  },

  getProductsBySupport(supportDocumentId, { page = 1, pageSize = 10, search = '' } = {}) {
    const params = {
      filters: { supports: { documentId: { $eq: supportDocumentId } } },
      populate: '*',
      'pagination[page]': page,
      'pagination[pageSize]': pageSize,
    };

    if (search) {
      params.filters.$or = [
        { reference: { $containsi: search } },
        { attributes: { $containsi: search } },
      ];
    }

    return apiClient.get('/products', { params });
  },

  getGridTemplates() {
    return apiClient.get('/grid-templates');
  },

  getProductMasks() {
    return apiClient.get('/product-masks');
  },

  getPage(id) {
    const params = {
      populate: {
        product_assignments: {
          populate: {
            products: true,
            masque: true
          }
        }
      }
    };
    return apiClient.get(`/pages/${id}`, { params });
  },

  updatePage(id, data) {
    return apiClient.put(`/pages/${id}`, { data });
  },

  async   updateCatalog(catalogId, data) {
    const response = await apiClient.put(`/supports/${catalogId}`, { data });
    return response.data;
  },

    me(){
      return apiClient.get('/users/me', {
        params: { 'populate[0]': 'role', 'populate[1]': 'budgets.logo' },
      });
    },

    getUsers() {
      return apiClient.get('/users');
    },

  

        exportPdfs(data) {

  

          return apiClient.post('/pages/export-pdfs', data);

  

        },
  getNotifications(userId) {
    const params = {
      filters: {
        user: {
          id: {
            $eq: userId,
          },
        },
      },
      populate: '*', // Populate all fields for now, can be optimized later
      sort: ['createdAt:desc'], // Sort by creation date, newest first
    };
    return apiClient.get('/notifications', { params });
  },
  updateNotification(id, data) {
    return apiClient.put(`/notifications/${id}`, { data });
  },

  getSupport(id) {    
    return apiClient.get(`/supports/${id}`);
  },

  getSupportPages(supportId) {
    return apiClient.get('/pages', {
      params: {
        filters: { support: { documentId: { $eq: supportId } } },
        populate: {
          product_assignments: {
            populate: {
              products: { populate: 'image' },
              masque: true,
            },
          },
          active_image_version: { populate: 'fichier_jpeg' },
        },
        sort: 'page_number:asc',
        'pagination[limit]': 200,
      },
    });
  },

  getFlatplanExport(supportId) {
    return apiClient.get(`/supports/${supportId}/flatplan-export`);
  },

  getMasques(charteId = null) {
    const params = {
      'pagination[limit]': 200,
      sort: 'name:asc',
      populate: 'charte',
    };

    if (charteId) {
      params['filters[charte][id][$eq]'] = charteId;
    }

    return apiClient.get('/masques', {
      params,
    });
  },

  createMasque(data) {
    return apiClient.post('/masques', { data });
  },

  updateMasque(documentId, data) {
    return apiClient.put(`/masques/${documentId}`, { data });
  },

  deleteMasque(documentId) {
    return apiClient.delete(`/masques/${documentId}`);
  },

  importExcel(supportId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/supports/${supportId}/import-excel`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  uploadMedia(supportId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/supports/${supportId}/upload-media`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};
